export enum AutoRefreshInterval {
    OFF = 0,
    ONE_MINUTE = 1,
    FIVE_MINUTES = 5,
    TEN_MINUTES = 10
}

export interface AutoRefreshConfig {
    /** Unique identifier for this instance */
    instanceId: string;
    /** Refresh interval in minutes */
    intervalMinutes: number;
    /** Whether auto-refresh is currently active */
    isActive: boolean;
    /** Function to call when refresh is triggered */
    refreshCallback: () => void;
    /** Internal timer ID for cleanup */
    timerId?: ReturnType<typeof globalThis.setTimeout>;
}

export class AutoRefreshService {
    private static readonly instances = new Map<string, AutoRefreshConfig>();
    private static readonly STORAGE_KEY_PREFIX = 'auto-refresh-';

    private static readonly AUTO_REFRESH_INTERVALS: {label: string; value: number}[] = [
        {label: 'Auto Refresh : OFF', value: AutoRefreshInterval.OFF},
        {label: 'Auto Refresh : 1 minute', value: AutoRefreshInterval.ONE_MINUTE},
        {label: 'Auto Refresh : 5 minutes', value: AutoRefreshInterval.FIVE_MINUTES},
        {label: 'Auto Refresh : 10 minutes', value: AutoRefreshInterval.TEN_MINUTES}
    ];

    // ===============================================================
    // ===============================================================

    /**
     * Get available refresh intervals
     */
    public static getAvailableIntervals(): {label: string; value: number}[] {
        return this.AUTO_REFRESH_INTERVALS;
    }

    /**
     * Register a new auto-refresh instance
     * @param instanceId Unique identifier for the instance
     * @param refreshCallback Function to call when refresh is triggered
     * @param defaultInterval Default refresh interval in minutes
     */
    public static registerInstance(
        instanceId: string,
        refreshCallback: () => void,
        defaultInterval = AutoRefreshInterval.FIVE_MINUTES
    ): void {
        if (!instanceId || instanceId.trim().length === 0) {
            throw new Error('instanceId cannot be empty or whitespace');
        }
        if (!refreshCallback || typeof refreshCallback !== 'function') {
            throw new Error('refreshCallback must be a valid function');
        }

        // Load saved interval from localStorage
        const savedInterval = this.loadIntervalFromStorage(instanceId);
        const intervalMinutes = savedInterval ?? defaultInterval;

        const config: AutoRefreshConfig = {
            instanceId: instanceId.trim(),
            intervalMinutes,
            isActive: intervalMinutes > AutoRefreshInterval.OFF,
            refreshCallback
        };

        this.instances.set(instanceId, config);

        if (config.isActive) {
            this.startTimer(config);
        }
    }

    /**
     * Unregister an auto-refresh instance
     * @param instanceId Unique identifier for the instance
     */
    public static unregisterInstance(instanceId: string): void {
        if (!instanceId) return;

        const config = this.instances.get(instanceId);
        if (config) {
            this.stopTimer(config);
            this.instances.delete(instanceId);
        }
    }

    /**
     * Update the refresh interval for an instance
     * @param instanceId Unique identifier for the instance
     * @param intervalMinutes New refresh interval in minutes
     */
    public static updateInterval(instanceId: string, intervalMinutes: number): void {
        if (!instanceId) return;

        const validIntervals = Object.values(AutoRefreshInterval);
        if (!validIntervals.includes(intervalMinutes)) {
            throw new Error(`intervalMinutes must be one of: ${validIntervals.join(', ')}`);
        }

        const config = this.instances.get(instanceId);
        if (config) {
            this.stopTimer(config);
            config.intervalMinutes = intervalMinutes;
            config.isActive = intervalMinutes > AutoRefreshInterval.OFF;

            // Save to localStorage
            this.saveIntervalToStorage(instanceId, intervalMinutes);

            if (config.isActive) {
                this.startTimer(config);
            }
        }
    }

    /**
     * Trigger immediate refresh for an instance
     * @param instanceId Unique identifier for the instance
     */
    public static triggerRefresh(instanceId: string): void {
        if (!instanceId) return;

        const config = this.instances.get(instanceId);
        if (config) {
            config.refreshCallback();

            // Restart timer if active
            if (config.isActive) {
                this.stopTimer(config);
                this.startTimer(config);
            }
        }
    }

    /**
     * Pause auto-refresh for an instance
     * @param instanceId Unique identifier for the instance
     */
    public static pauseInstance(instanceId: string): void {
        if (!instanceId) return;

        const config = this.instances.get(instanceId);
        if (config) {
            config.isActive = false;
            this.stopTimer(config);
        }
    }

    /**
     * Resume auto-refresh for an instance
     * @param instanceId Unique identifier for the instance
     */
    public static resumeInstance(instanceId: string): void {
        if (!instanceId) return;

        const config = this.instances.get(instanceId);
        if (config && config.intervalMinutes > AutoRefreshInterval.OFF) {
            config.isActive = true;
            this.startTimer(config);
        }
    }

    /**
     * Get configuration for an instance
     * @param instanceId Unique identifier for the instance
     * @returns Instance configuration or undefined if not found
     */
    public static getInstanceConfig(instanceId: string): AutoRefreshConfig | undefined {
        if (!instanceId) return undefined;
        return this.instances.get(instanceId);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get all auto-refresh localStorage keys
     * @returns Array of instance IDs that have stored settings
     */
    public static getAllStorageKeys(): string[] {
        try {
            const keys: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key?.startsWith(this.STORAGE_KEY_PREFIX)) continue;

                keys.push(key.substring(this.STORAGE_KEY_PREFIX.length));
            }
            return keys;
        } catch (_error) {
            // Intentionally silent fail - localStorage may not be available in some environments
            return [];
        }
    }

    /**
     * Clean up orphaned localStorage entries (entries that don't have active instances)
     */
    public static cleanupOrphanedStorage(): void {
        try {
            const allStorageKeys = this.getAllStorageKeys();
            const activeInstanceIds = new Set(this.instances.keys());

            for (const instanceId of allStorageKeys) {
                if (activeInstanceIds.has(instanceId)) continue;
                this.removeIntervalFromStorage(instanceId);
            }
        } catch (_error) {
            // Intentionally silent fail - localStorage may not be available in some environments
        }
    }

    /**
     * Clear all auto-refresh localStorage entries
     */
    public static clearAllStorage(): void {
        try {
            const allStorageKeys = this.getAllStorageKeys();
            for (const instanceId of allStorageKeys) {
                this.removeIntervalFromStorage(instanceId);
            }
        } catch (_error) {
            // Intentionally silent fail - localStorage may not be available in some environments
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Start the timer for an instance
     * @param config Instance configuration
     */
    private static startTimer(config: AutoRefreshConfig): void {
        this.stopTimer(config);
        const intervalMs = config.intervalMinutes * 60 * 1000;
        config.timerId = globalThis.setTimeout(() => {
            config.refreshCallback();
            if (config.isActive) {
                this.startTimer(config); // Restart timer
            }
        }, intervalMs);
    }

    /**
     * Stop the timer for an instance
     * @param config Instance configuration
     */
    private static stopTimer(config: AutoRefreshConfig): void {
        if (config.timerId) {
            globalThis.clearTimeout(config.timerId);
            config.timerId = undefined;
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Save interval to localStorage
     * @param instanceId Unique identifier for the instance
     * @param intervalMinutes Interval to save
     */
    private static saveIntervalToStorage(instanceId: string, intervalMinutes: number): void {
        try {
            localStorage.setItem(this.STORAGE_KEY_PREFIX + instanceId, intervalMinutes.toString());
        } catch (_error) {
            // Intentionally silent fail - localStorage may not be available in some environments
        }
    }

    /**
     * Load interval from localStorage
     * @param instanceId Unique identifier for the instance
     * @returns Saved interval or null if not found
     */
    private static loadIntervalFromStorage(instanceId: string): number | null {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY_PREFIX + instanceId);
            return saved ? Number.parseInt(saved, 10) : null;
        } catch (_error) {
            return null;
            // Intentionally silent fail - localStorage may not be available in some environments
        }
    }

    /**
     * Remove interval from localStorage
     * @param instanceId Unique identifier for the instance
     */
    private static removeIntervalFromStorage(instanceId: string): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY_PREFIX + instanceId);
        } catch (_error) {
            // Intentionally silent fail - localStorage may not be available in some environments
        }
    }
}
